import { Button, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger } from '@fluentui/react-components'
import { Delete24Regular, Edit24Regular, Navigation24Regular } from '@fluentui/react-icons'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { createRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { AddEntityTypeForm } from '@/ui/Forms/AddEntityTypeForm'
import { OverlayDrawerComponent } from '@/ui/OverlayDrawerComponent/OverlayDrawerComponent'

type MessagesEntityTypesExistingEntityType = RestSchemas['Messages.EntityTypes.ExistingEntityType']

export function EntityCardActionsMenu({ entityType }: { entityType: MessagesEntityTypesExistingEntityType }) {
  const queryClient = useQueryClient()
  const [drawerOpen, setDrawerOpen] = useState<'none' | 'editEntityType' | 'deleteEntityType'>('none')

  const { applicationId, id } = entityType

  const { deleteEntityTypeApiDeleteEntityType, getEntityTypeApiGetEntityTypesByApplicationId } = createRestClient()

  const invalidateEntityTypes = () => {
    getEntityTypeApiGetEntityTypesByApplicationId.invalidateQueries(
      { parameters: { path: { applicationId } } },
      queryClient
    )
  }

  const { mutate: deleteEntityType, isPending: isDeleting } = deleteEntityTypeApiDeleteEntityType.useMutation(
    { path: { id } },
    {
      onSuccess: () => {
        invalidateEntityTypes()
        setDrawerOpen('none')
      },
    }
  )

  return (
    <div className="justify-self-end">
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <Button icon={<Navigation24Regular />} />
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem
              className="!items-center"
              onClick={() => setDrawerOpen('editEntityType')}
              icon={<Edit24Regular />}
            >
              Edit entity type
            </MenuItem>
            <MenuItem
              className="!items-center"
              onClick={() => setDrawerOpen('deleteEntityType')}
              icon={<Delete24Regular />}
            >
              Remove entity type
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>

      {/* Edit Entity Type Drawer */}
      <OverlayDrawerComponent
        open={drawerOpen === 'editEntityType'}
        size="medium"
        setOpen={(open) => setDrawerOpen(open ? 'editEntityType' : 'none')}
        title="Edit Entity Type"
        content={
          <AddEntityTypeForm
            applicationId={applicationId}
            selectedEntityType={entityType}
            edit
            onSubmit={() => {
              setDrawerOpen('none')
              invalidateEntityTypes()
            }}
          />
        }
      />

      {/* Delete Entity Type Confirmation Drawer */}
      <OverlayDrawerComponent
        open={drawerOpen === 'deleteEntityType'}
        setOpen={(open) => setDrawerOpen(open ? 'deleteEntityType' : 'none')}
        title="Confirm Delete"
        content={
          <div>
            <p>Are you sure you want to delete this entity type?</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
              <Button disabled={isDeleting} appearance="secondary" onClick={() => setDrawerOpen('none')}>
                Cancel
              </Button>
              <Button
                disabled={isDeleting}
                appearance="primary"
                onClick={() => {
                  deleteEntityType()
                  invalidateEntityTypes()
                }}
              >
                Confirm
              </Button>
            </div>
          </div>
        }
      />
    </div>
  )
}
